import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Command, Key, Library, Sparkles, Zap } from "lucide-react";
import { Logo } from "~/components/logo";
import { ThemeToggle } from "~/components/theme-toggle";
import { Button } from "~/components/ui/button";
import { authQueryOptions } from "~/lib/auth/queries";

export const Route = createFileRoute("/")({
  component: HomePage,
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData({
      ...authQueryOptions(),
      revalidateIfStale: true,
    });
    if (user) {
      throw redirect({
        to: "/dashboard",
      });
    }
  },
});

function HomePage() {
  return (
    <div className="flex min-h-screen flex-col font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <Logo size="md" showText={false} className="text-xl" />
            <span>quickprompt.dev</span>
          </div>
          <nav className="flex items-center gap-4">
            <ThemeToggle />
            <Button render={<Link to="/login" />} className="font-medium">
              Log in
            </Button>
          </nav>
        </div>
      </header>

      <main className="relative flex flex-1 flex-col overflow-hidden">
        {/* Gradient Background Effects */}
        <div className="bg-primary/10 pointer-events-none absolute top-0 left-1/2 -z-10 h-[500px] w-[1000px] -translate-x-1/2 rounded-full opacity-50 blur-3xl dark:opacity-20" />

        {/* Hero Section */}
        <section className="animate-in fade-in slide-in-from-bottom-4 flex flex-col items-center justify-center gap-8 px-4 pt-24 pb-16 text-center duration-700 sm:px-8">
          <div className="max-w-3xl space-y-4">
            <div className="focus:ring-ring bg-secondary text-secondary-foreground hover:bg-secondary/80 mb-4 inline-flex items-center gap-1 rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none">
              <Sparkles size={12} className="text-orange-500" />
              <span>v1.0 Public Beta</span>
            </div>
            <h1 className="text-foreground text-4xl leading-[1.1] font-extrabold tracking-tight text-balance sm:text-6xl md:text-7xl">
              Your AI, your way. <br className="hidden sm:block" />
              <span className="text-primary bg-clip-text">
                One keyboard shortcut away.
              </span>
            </h1>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed text-balance sm:text-xl">
              The "Raycast for AI prompts". A lightweight, lightning-fast interface
              designed for power users who want control, speed, and privacy.
            </p>
          </div>

          <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              render={<Link to="/login" />}
              size="lg"
              className="shadow-primary/20 h-12 px-8 text-base shadow-lg"
            >
              Get Started for Free
            </Button>
          </div>

          {/* Visual Showcase (Mock Command Palette) */}
          <div className="perspective-1000 mt-12 w-full max-w-4xl">
            <div className="bg-card/50 animate-in fade-in zoom-in slide-in-from-bottom-8 fill-mode-forwards relative scale-95 rotate-x-12 transform-gpu overflow-hidden rounded-xl border border-t-white/10 opacity-0 shadow-2xl ring-1 ring-white/10 backdrop-blur-sm delay-300 duration-1000 dark:border-t-white/5">
              {/* Window Controls */}
              <div className="bg-muted/20 flex h-10 items-center gap-2 border-b px-4">
                <div className="h-3 w-3 rounded-full bg-red-400/80" />
                <div className="h-3 w-3 rounded-full bg-yellow-400/80" />
                <div className="h-3 w-3 rounded-full bg-green-400/80" />
              </div>
              {/* Search Input */}
              <div className="flex items-center gap-3 border-b p-4">
                <Command className="text-muted-foreground" />
                <span className="text-muted-foreground text-lg">
                  Type a command or search...
                </span>
                <div className="ml-auto flex gap-1">
                  <kbd className="bg-muted text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium opacity-100 select-none">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </div>
              </div>
              {/* Mock List */}
              <div className="space-y-1 p-2">
                <div className="bg-accent text-accent-foreground flex items-center gap-3 rounded-md p-3">
                  <div className="bg-primary/20 text-primary flex h-8 w-8 items-center justify-center rounded">
                    <Zap size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">Generate Code Snippet</span>
                    <span className="text-xs opacity-70">Development</span>
                  </div>
                  <span className="ml-auto text-xs opacity-50">Enter</span>
                </div>
                <div className="hover:bg-muted/50 flex items-center gap-3 rounded-md p-3 opacity-60 transition-colors">
                  <div className="bg-muted flex h-8 w-8 items-center justify-center rounded">
                    <Library size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">Search Prompts</span>
                    <span className="text-xs opacity-70">Library</span>
                  </div>
                </div>
                <div className="hover:bg-muted/50 flex items-center gap-3 rounded-md p-3 opacity-60 transition-colors">
                  <div className="bg-muted flex h-8 w-8 items-center justify-center rounded">
                    <Key size={18} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium">Manage API Keys</span>
                    <span className="text-xs opacity-70">Settings</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="bg-muted/30 py-24">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <FeatureCard
                icon={<Zap className="text-orange-500" />}
                title="Lightning Fast"
                description="No loading states. No spinners. Instant streaming responses designed for flow state."
              />
              <FeatureCard
                icon={<Key className="text-blue-500" />}
                title="Bring Your Own Key"
                description="Use your own API keys for OpenAI, Anthropic, and more. Full control, no markups."
              />
              <FeatureCard
                icon={<Library className="text-purple-500" />}
                title="Prompt Library"
                description="Organize your perfect prompts. Tag, search, and reuse them with a single keystroke."
              />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="text-muted-foreground bg-background border-t py-8 text-center text-sm">
        <div className="container mx-auto px-4">
          <p>© {new Date().getFullYear()} quickprompt.dev. Built for builders.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="bg-card flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
      <div className="bg-accent mb-4 flex h-12 w-12 items-center justify-center rounded-xl">
        {icon}
      </div>
      <h3 className="mb-2 text-xl font-bold">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}
